import type {
  AIDraftService,
  AIToolsConfigService,
} from '@affine/core/modules/ai-button';
import type { AIDraftState } from '@affine/core/modules/ai-button/services/ai-draft';
import type { AIModelService } from '@affine/core/modules/ai-button/services/models';
import {
  type ServerService,
  type SubscriptionService,
  workspaceApiFetch,
} from '@affine/core/modules/cloud';
import type { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import type { FeatureFlagService } from '@affine/core/modules/feature-flag';
import type { PeekViewService } from '@affine/core/modules/peek-view';
import { subscribeCopilotHistoryEvents } from '@affine/core/modules/streamlain/copilot/copilot-history-realtime-events';
import type { AppThemeService } from '@affine/core/modules/theme';
import type { CopilotChatHistoryFragment } from '@affine/graphql';
import { SignalWatcher, WithDisposable } from '@blocksuite/affine/global/lit';
import { type EditorHost, ShadowlessElement } from '@blocksuite/affine/std';
import type { ExtensionType } from '@blocksuite/affine/store';
import type { NotificationService } from '@blocksuite/affine-shared/services';
import { type Signal } from '@preact/signals-core';
import type { CopilotHistoryChangedEventV1 } from '@streamlain/copilot-realtime-contract';
import type { GoalTreePatch } from '@streamlain/view-config';
import {
  css,
  html,
  nothing,
  type PropertyValues,
  type TemplateResult,
} from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { createRef, type Ref, ref } from 'lit/directives/ref.js';
import { styleMap } from 'lit/directives/style-map.js';
import { pick } from 'lodash-es';

import type { WorkspaceDocSyncEngine } from '../../../../modules/streamlain/stream-objects';
import type { BotOptionData } from '../../chat-panel/ai-title';
import { HISTORY_IMAGE_ACTIONS } from '../../chat-panel/const';
import { type AIChatParams, AIProvider } from '../../provider/ai-provider';
import {
  AI_CODE_SELECTION_CONTEXT_EVENT,
  isCodeSelectionContextEvent,
} from '../../utils/code-selection-context';
import { extractSelectedContent } from '../../utils/extract';
import { shouldPreserveCurrentStreamObjects } from '../../utils/stream-objects';
import type { SearchMenuConfig } from '../ai-chat-add-context';
import type { DocDisplayConfig } from '../ai-chat-chips';
import type {
  AINetworkSearchConfig,
  AIReasoningConfig,
} from '../ai-chat-input';
import type {
  AIChatMessages,
  ChatAction,
  ChatMessage,
  HistoryMessage,
  StreamObject,
} from '../ai-chat-messages';
import { isChatMessage } from '../ai-chat-messages';
import {
  extractLatestGoalPlanFromMessages,
  extractLatestGoalReferenceFromMessages,
  extractLatestGoalTreeFromMessages,
  type GoalListSummary,
  type GoalPlanHandle,
  parseGoalListSummaryCandidate,
  parseGoalPlanHandleCandidate,
  parseGoalReferenceCandidate,
} from '../goal-tree/goal-tree-context';
import {
  historyChangeAffectsView,
  HistoryRefreshScheduler,
} from './history-refresh-scheduler';
import type {
  CanvasAIChatContextMode,
  ChatContextValue,
  ExternalChatContextDoc,
  WorkflowAttachment,
} from './type';

const DEFAULT_CHAT_CONTEXT_VALUE: ChatContextValue = {
  quote: '',
  images: [],
  abortController: null,
  messages: [],
  status: 'idle',
  error: null,
  markdown: '',
  snapshot: null,
  attachments: [],
  combinedElementsMarkdown: null,
  docs: [],
  html: null,
  databaseAttachments: [], // sl-57s
  automationAttachments: [],
  workflowAttachments: [], // sl-dpq2.5
  codeSelections: [],
  pdfAttachments: [], // sl-w252
  pdfContextAttachments: [], // sl-w252
  canvasSelections: [],
};

export class AIChatContent extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    ai-chat-content {
      display: flex;
      flex-direction: column;
      justify-content: center;
      height: 100%;

      ai-chat-messages {
        flex: 1;
        overflow-y: auto;
        padding: 0 var(--h-padding);
        transition:
          flex-grow 0.32s cubic-bezier(0.07, 0.83, 0.46, 1),
          padding-top 0.32s ease,
          padding-bottom 0.32s ease;
      }
      ai-chat-messages.independent-mode.no-message {
        flex-grow: 0;
        flex-shrink: 0;
        overflow-y: visible;
      }
    }
    chat-panel-split-view {
      height: 100%;
      width: 100%;
      container-type: inline-size;
      container-name: chat-panel-split-view;
    }
    .chat-panel-main {
      --h-padding: 8px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      height: 100%;
      width: 100%;
      padding: 8px calc(24px - var(--h-padding)) 0 calc(24px - var(--h-padding));
      max-width: 800px;
      margin: 0 auto;
    }

    .chat-panel-messages-layer {
      --chat-panel-goal-overlay-gap: var(--h-padding);
      --chat-panel-goal-overlay-bottom-reserve: 64px;
      position: relative;
      display: flex;
      flex: 1 1 auto;
      min-height: 0;
      flex-direction: column;
    }

    .chat-panel-goal-overlay {
      position: absolute;
      inset: 0;
      z-index: 20;
      overflow: hidden;
      pointer-events: none;
    }

    .chat-panel-goal-overlay[data-open='true'] {
      pointer-events: auto;
    }

    .chat-panel-goal-overlay-backdrop {
      position: absolute;
      inset: 0;
      background: transparent;
    }

    .chat-panel-goal-overlay-panel {
      position: absolute;
      top: var(--chat-panel-goal-overlay-gap);
      right: 0;
      bottom: calc(
        var(--chat-panel-goal-overlay-bottom-reserve) +
          var(--chat-panel-goal-overlay-gap)
      );
      width: min(430px, calc(100% - var(--chat-panel-goal-overlay-gap)));
      pointer-events: auto;
      transform: translateX(100%);
      transition: transform 200ms ease-out;
    }

    .chat-panel-goal-overlay[data-open='true'] .chat-panel-goal-overlay-panel {
      transform: translateX(0);
    }

    .chat-panel-goal-overlay-panel goal-tree-panel {
      display: block;
      width: 100%;
      height: 100%;
    }

    ai-chat-composer {
      position: relative;
      z-index: 30;
      padding: 0 var(--h-padding);
    }

    @container chat-panel-split-view (width < 540px) {
      .chat-panel-main {
        padding: 8px calc(12px - var(--h-padding)) 0
          calc(12px - var(--h-padding));
      }

      .chat-panel-goal-overlay-panel {
        left: var(--chat-panel-goal-overlay-gap);
        width: auto;
      }
    }
  `;

  @property({ attribute: false })
  accessor independentMode: boolean | undefined;

  @property({ attribute: false })
  accessor canvasAIChatContextMode: CanvasAIChatContextMode = 'lineage-only';

  @property({ attribute: false })
  accessor onboardingOffsetY!: number;

  @property({ attribute: false })
  accessor host: EditorHost | null | undefined;

  @property({ attribute: false })
  accessor session!: CopilotChatHistoryFragment | null | undefined;

  @property({ attribute: false })
  accessor createSession!: () => Promise<
    CopilotChatHistoryFragment | undefined
  >;

  @property({ attribute: false })
  accessor workspaceId!: string;

  @property({ attribute: false })
  accessor docId: string | undefined;

  @property({ attribute: false })
  accessor networkSearchConfig!: AINetworkSearchConfig;

  @property({ attribute: false })
  accessor reasoningConfig!: AIReasoningConfig;

  @property({ attribute: false })
  accessor searchMenuConfig!: SearchMenuConfig;

  @property({ attribute: false })
  accessor docDisplayConfig!: DocDisplayConfig;

  @property({ attribute: false })
  accessor extensions!: ExtensionType[];

  @property({ attribute: false })
  accessor serverService!: ServerService;

  @property({ attribute: false })
  accessor affineFeatureFlagService!: FeatureFlagService;

  @property({ attribute: false })
  accessor affineWorkspaceDialogService!: WorkspaceDialogService;

  @property({ attribute: false })
  accessor affineThemeService!: AppThemeService;

  @property({ attribute: false })
  accessor notificationService!: NotificationService;

  @property({ attribute: false })
  accessor aiDraftService: AIDraftService | undefined;

  @property({ attribute: false })
  accessor aiToolsConfigService!: AIToolsConfigService;

  @property({ attribute: false })
  accessor aiModelService!: AIModelService;

  @property({ attribute: false })
  accessor onContextChange!: (context: Partial<ChatContextValue>) => void;

  @property({ attribute: false })
  accessor externalContextDocs: ExternalChatContextDoc[] = [];

  @property({ attribute: false })
  accessor onOpenDoc!: (docId: string, sessionId?: string) => void;

  @property({ attribute: false })
  accessor width: Signal<number | undefined> | undefined;

  @property({ attribute: false })
  accessor peekViewService!: PeekViewService;

  @property({ attribute: false })
  accessor subscriptionService!: SubscriptionService;

  @property({ attribute: false })
  accessor workspaceDocSyncEngine: WorkspaceDocSyncEngine | null | undefined;

  @property({ attribute: false })
  accessor onAISubscribe!: () => Promise<void>;

  @property({ attribute: false })
  accessor onOpenSettings: (() => void) | undefined;

  @property({ attribute: false })
  accessor noKeysTitle: string | undefined;

  @property({ attribute: false })
  accessor noKeysDescription: string | undefined;

  @property({ attribute: false })
  accessor noKeysButtonText: string | undefined;

  @property({ attribute: false })
  accessor bots: BotOptionData[] = [];

  @property({ attribute: false })
  accessor onSelectBot: ((botId: string | null) => void) | undefined;

  @property({ attribute: false })
  accessor targetModeEnabled = false;

  @property({ attribute: false })
  accessor activeWorkflowAttachment: WorkflowAttachment | null = null;

  @property({ attribute: false })
  accessor activeWorkflowContextForce = 0;

  @state()
  accessor chatContextValue: ChatContextValue = DEFAULT_CHAT_CONTEXT_VALUE;

  @state()
  accessor isHistoryLoading = false;

  @state()
  private accessor showPreviewPanel = false;

  @state()
  private accessor previewPanelContent: TemplateResult<1> | null = null;

  @state()
  private accessor goalTreeOpen = false;

  @state()
  private accessor activeGoalPlanOverride: GoalPlanHandle | null = null;

  @state()
  private accessor goalHistory: readonly GoalListSummary[] = [];

  @state()
  private accessor goalHistoryLoading = false;

  private activeGoalId: string | null = null;

  private dismissedWorkflowContextKey: string | null = null;

  private lastActiveWorkflowContextKey: string | null = null;

  private syncingActiveWorkflowContext = false;

  private activeGoalPollTimer: number | null = null;

  private activeGoalRefreshInFlight = false;

  private readonly chatMessagesRef: Ref<AIChatMessages> =
    createRef<AIChatMessages>();

  // request counter to track the latest request
  private updateHistoryCounter = 0;

  private readonly historyRefresh = new HistoryRefreshScheduler(
    () => this.isSafeToRefreshHistory(),
    () => this.updateHistory().catch(console.error)
  );

  private lastHistorySignature = '';

  private lastScrollTop: number | undefined;

  private lastEmittedGoalPlanKey: string | null = null;

  get messages() {
    return this.chatContextValue.messages.filter(item => {
      return (
        isChatMessage(item) ||
        item.messages?.length === 3 ||
        (HISTORY_IMAGE_ACTIONS.includes(item.action) &&
          item.messages?.length === 2)
      );
    });
  }

  get showActions() {
    return false;
  }

  private readonly updateHistory = async () => {
    const currentRequest = ++this.updateHistoryCounter;
    if (!AIProvider.histories) {
      return;
    }

    const sessionId = this.session?.sessionId;
    const [histories, actions] = await Promise.all([
      sessionId
        ? AIProvider.histories.chats(this.workspaceId, sessionId)
        : Promise.resolve([]),
      this.docId && this.showActions
        ? AIProvider.histories.actions(this.workspaceId, this.docId)
        : Promise.resolve([]),
    ]);

    // Check if this is still the latest request
    if (currentRequest !== this.updateHistoryCounter) {
      return;
    }

    const chatActions = (actions || []) as ChatAction[];
    const chatMessages = (histories?.[0]?.messages || []) as ChatMessage[];
    const messages = this.preserveCurrentStreamObjects(
      [...chatActions, ...chatMessages].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      )
    );
    const historySignature = this.getHistorySignature(messages);

    if (historySignature === this.lastHistorySignature) {
      return;
    }

    const shouldScrollToEnd = this.shouldScrollToEndAfterHistoryRefresh(
      messages.length
    );
    this.lastHistorySignature = historySignature;
    this.updateContext({ messages });
    if (shouldScrollToEnd) {
      this.chatMessagesRef.value?.scrollToEnd();
    }
  };

  private getHistorySignature(messages: HistoryMessage[]) {
    return messages
      .map(message => {
        if (isChatMessage(message)) {
          return [
            'message',
            message.id ?? '',
            message.role,
            message.createdAt ?? '',
            message.content,
            this.getStreamObjectsSignature(message.streamObjects),
          ].join(':');
        }
        return [
          'action',
          message.action,
          message.createdAt ?? '',
          message.messages?.length ?? 0,
        ].join(':');
      })
      .join('|');
  }

  private getStreamObjectsSignature(streamObjects?: StreamObject[] | null) {
    if (!streamObjects?.length) {
      return '';
    }

    return JSON.stringify(streamObjects);
  }

  private preserveCurrentStreamObjects(messages: HistoryMessage[]) {
    const currentChatMessages =
      this.chatContextValue.messages.filter(isChatMessage);
    const currentById = new Map(
      currentChatMessages
        .filter(message => !!message.id && !!message.streamObjects?.length)
        .map(message => [message.id, message])
    );
    const nextMessages = messages.slice();
    let currentIndex = currentChatMessages.length - 1;

    for (let index = nextMessages.length - 1; index >= 0; index--) {
      const message = nextMessages[index];
      if (!isChatMessage(message)) {
        continue;
      }

      let current = message.id ? currentById.get(message.id) : undefined;
      if (!current) {
        while (currentIndex >= 0) {
          const candidate = currentChatMessages[currentIndex];
          currentIndex--;
          if (candidate?.role === message.role) {
            current = candidate;
            break;
          }
        }
      }
      if (!current?.streamObjects?.length) {
        continue;
      }

      if (
        !shouldPreserveCurrentStreamObjects({
          currentStreamObjects: current.streamObjects,
          persistedContent: message.content,
          persistedStreamObjects: message.streamObjects,
          persistedToolResults: message.toolData?.toolResults,
        })
      ) {
        continue;
      }

      nextMessages[index] = {
        ...message,
        streamObjects: current.streamObjects,
      };
    }

    return nextMessages;
  }

  private isSafeToRefreshHistory() {
    const { status } = this.chatContextValue;
    return (
      !!this.session?.sessionId &&
      (status === 'idle' || status === 'success') &&
      document.visibilityState === 'visible'
    );
  }

  private shouldScrollToEndAfterHistoryRefresh(nextMessageCount: number) {
    if (nextMessageCount <= this.messages.length) {
      return false;
    }

    const chatMessages = this.chatMessagesRef.value;
    if (!chatMessages) {
      return false;
    }

    return (
      chatMessages.scrollHeight -
        chatMessages.scrollTop -
        chatMessages.clientHeight <
      96
    );
  }

  private readonly handleCopilotHistoryChanged = (
    event: CopilotHistoryChangedEventV1
  ) => {
    const view = {
      sessionId: this.session?.sessionId,
      docId: this.docId,
      showActions: this.showActions,
    };
    if (historyChangeAffectsView(event, view)) {
      this.historyRefresh.request();
    }
  };

  private readonly handleHistoryVisibilityChange = () => {
    this.historyRefresh.flush();
  };

  private subscribeHistoryUpdates() {
    this._disposables.add(
      subscribeCopilotHistoryEvents({
        onChanged: this.handleCopilotHistoryChanged,
        onReconnected: () => this.historyRefresh.request(),
      })
    );
    document.addEventListener(
      'visibilitychange',
      this.handleHistoryVisibilityChange
    );
    this._disposables.add(() =>
      document.removeEventListener(
        'visibilitychange',
        this.handleHistoryVisibilityChange
      )
    );
  }

  private readonly updateActions = async () => {
    if (!this.docId || !AIProvider.histories || !this.showActions) {
      return;
    }
    const actions = await AIProvider.histories.actions(
      this.workspaceId,
      this.docId
    );
    if (actions && actions.length) {
      const chatMessages = this.chatContextValue.messages.filter(message =>
        isChatMessage(message)
      );
      const chatActions = actions as ChatAction[];
      const messages: HistoryMessage[] = [...chatMessages, ...chatActions];
      this.updateContext({
        messages: messages.sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        ),
      });
    }
  };

  private readonly updateContext = (context: Partial<ChatContextValue>) => {
    const previousWorkflowIds = new Set(
      (this.chatContextValue.workflowAttachments ?? []).map(
        attachment => attachment.workflowId
      )
    );
    const createdWorkflowAdded = context.workflowAttachments?.some(
      attachment =>
        attachment.source === 'created' &&
        !previousWorkflowIds.has(attachment.workflowId)
    );
    if (
      !this.syncingActiveWorkflowContext &&
      context.workflowAttachments &&
      this.activeWorkflowAttachment?.contextKey &&
      !context.workflowAttachments.some(
        attachment =>
          attachment.contextKey === this.activeWorkflowAttachment?.contextKey
      )
    ) {
      this.dismissedWorkflowContextKey =
        this.activeWorkflowAttachment.contextKey;
    }
    this.chatContextValue = { ...this.chatContextValue, ...context };
    this.onContextChange?.(context);
    this.updateDraft(context).catch(console.error);
    if (createdWorkflowAdded) {
      window.dispatchEvent(new CustomEvent('streamlain:workflow-updated'));
    }
  };

  private _syncActiveWorkflowContext(force: boolean): void {
    const attachment = this.activeWorkflowAttachment;
    const nextKey = attachment?.contextKey ?? null;
    if (nextKey !== this.lastActiveWorkflowContextKey) {
      this.dismissedWorkflowContextKey = null;
      this.lastActiveWorkflowContextKey = nextKey;
    }
    if (force) {
      this.dismissedWorkflowContextKey = null;
    }

    const current = this.chatContextValue.workflowAttachments ?? [];
    const retained = current.filter(
      item =>
        item.automationBlockId ||
        (!item.autoSelected &&
          (!attachment || item.workflowId !== attachment.workflowId))
    );
    const next =
      attachment && this.dismissedWorkflowContextKey !== nextKey
        ? [...retained, attachment]
        : retained;

    if (
      current.length === next.length &&
      current.every((item, index) => item === next[index])
    ) {
      return;
    }

    this.syncingActiveWorkflowContext = true;
    try {
      this.updateContext({ workflowAttachments: next });
    } finally {
      this.syncingActiveWorkflowContext = false;
    }
  }

  private _resolveGoalPlan(): GoalPlanHandle | null {
    if (!this.targetModeEnabled) return null;
    const contextPlan = extractLatestGoalPlanFromMessages(
      this.chatContextValue.messages
    );
    if (
      this.activeGoalPlanOverride &&
      (!contextPlan ||
        contextPlan.goalId === this.activeGoalPlanOverride.goalId)
    ) {
      return this.activeGoalPlanOverride;
    }
    return contextPlan;
  }

  private _resolveGoalTree() {
    return (
      this._resolveGoalPlan()?.goalTree ??
      extractLatestGoalTreeFromMessages(this.chatContextValue.messages)
    );
  }

  private _stopActiveGoalEvents(): void {
    if (this.activeGoalPollTimer !== null) {
      window.clearInterval(this.activeGoalPollTimer);
      this.activeGoalPollTimer = null;
    }
  }

  private _startActiveGoalEvents(goalId: string): void {
    if (!this.targetModeEnabled) return;
    this._stopActiveGoalEvents();
    void this._refreshActiveGoal(goalId);
    this.activeGoalPollTimer = window.setInterval(() => {
      void this._refreshActiveGoal(goalId);
    }, 1500);
  }

  private async _refreshActiveGoal(goalId: string): Promise<void> {
    if (!this.targetModeEnabled) return;
    if (this.activeGoalRefreshInFlight || this.activeGoalId !== goalId) return;
    this.activeGoalRefreshInFlight = true;
    try {
      const response = await workspaceApiFetch(
        `/api/workspace/${encodeURIComponent(this.workspaceId)}/agent-goals/${encodeURIComponent(goalId)}`,
        undefined,
        {
          workspaceId: this.workspaceId,
          category: 'ai-bootstrap',
          mode: 'background',
          priority: 'background',
        }
      );
      if (!response.ok || this.activeGoalId !== goalId) return;
      const goal = (await response.json()) as unknown;
      const nextPlan = parseGoalPlanHandleCandidate({ goal });
      if (nextPlan) {
        this.activeGoalPlanOverride = nextPlan;
        this._emitGoalPlanUpdate(nextPlan);
      }
      const status =
        nextPlan?.status ?? parseGoalReferenceCandidate({ goal })?.status;
      if (
        status === 'completed' ||
        status === 'failed' ||
        status === 'cancelled'
      ) {
        this._stopActiveGoalEvents();
      }
    } finally {
      this.activeGoalRefreshInFlight = false;
    }
  }

  private _syncActiveGoalFromMessages(): void {
    if (!this.targetModeEnabled) {
      this._stopActiveGoalEvents();
      this.activeGoalId = null;
      this.activeGoalPlanOverride = null;
      return;
    }
    const reference = extractLatestGoalReferenceFromMessages(
      this.chatContextValue.messages
    );
    if (!reference) return;
    if (this.activeGoalId !== reference.goalId) {
      this._stopActiveGoalEvents();
      this.activeGoalId = reference.goalId;
      this.activeGoalPlanOverride = null;
      this._startActiveGoalEvents(reference.goalId);
    }
    if (
      reference.status === 'queued' ||
      reference.status === 'running' ||
      !this.activeGoalPlanOverride
    ) {
      void this._refreshActiveGoal(reference.goalId);
    }
  }

  private readonly _handleGoalTreeToggle = (event: Event) => {
    if (!this.targetModeEnabled) return;
    const detail = (event as CustomEvent<{ workspaceId?: string }>).detail;
    if (detail?.workspaceId && detail.workspaceId !== this.workspaceId) return;
    this.goalTreeOpen = !this.goalTreeOpen;
    if (this.goalTreeOpen) void this._loadGoalHistory();
  };

  private async _loadGoalHistory(): Promise<void> {
    if (!this.targetModeEnabled) return;
    if (this.goalHistoryLoading) return;
    this.goalHistoryLoading = true;
    try {
      const response = await workspaceApiFetch(
        `/api/workspace/${encodeURIComponent(this.workspaceId)}/agent-goals?limit=50`,
        { credentials: 'include' },
        {
          workspaceId: this.workspaceId,
          category: 'ai-bootstrap',
          mode: 'background',
          priority: 'visible',
        }
      );
      if (!response.ok) return;
      const payload = (await response.json()) as unknown;
      this.goalHistory = Array.isArray(payload)
        ? payload.flatMap(item => {
            const parsed = parseGoalListSummaryCandidate(item);
            return parsed ? [parsed] : [];
          })
        : [];
    } finally {
      this.goalHistoryLoading = false;
    }
  }

  private readonly _selectGoalFromHistory = async (goalId: string) => {
    const response = await workspaceApiFetch(
      `/api/workspace/${encodeURIComponent(this.workspaceId)}/agent-goals/${encodeURIComponent(goalId)}`,
      { credentials: 'include' },
      {
        workspaceId: this.workspaceId,
        category: 'foreground-action',
        mode: 'foreground',
        priority: 'foreground',
      }
    );
    if (!response.ok) {
      throw new Error(`Target load failed with HTTP ${response.status}`);
    }
    const goal = (await response.json()) as unknown;
    const nextPlan = parseGoalPlanHandleCandidate({ goal });
    if (!nextPlan) throw new Error('Target has no readable plan');
    this.activeGoalId = goalId;
    this.activeGoalPlanOverride = nextPlan;
    this._emitGoalPlanUpdate(nextPlan);
    if (
      nextPlan.status === 'queued' ||
      nextPlan.status === 'running' ||
      nextPlan.status === 'waiting_for_confirmation' ||
      nextPlan.status === 'waiting_for_user' ||
      nextPlan.status === 'paused'
    ) {
      this._startActiveGoalEvents(goalId);
    } else {
      this._stopActiveGoalEvents();
    }
  };

  private readonly _closeGoalTree = () => {
    this.goalTreeOpen = false;
  };

  private readonly _handleGoalTreePatch = async (patch: GoalTreePatch) => {
    const goalPlan = this._resolveGoalPlan();
    if (!goalPlan) {
      throw new Error('No active agent goal is available for this patch');
    }
    const response = await workspaceApiFetch(
      `/api/workspace/${encodeURIComponent(this.workspaceId)}/agent-goals/${encodeURIComponent(goalPlan.goalId)}/resume`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ resumeData: { patches: [patch] } }),
      },
      {
        workspaceId: this.workspaceId,
        category: 'foreground-action',
        mode: 'foreground',
        priority: 'foreground',
      }
    );
    if (!response.ok) {
      throw new Error(`Agent goal resume failed with HTTP ${response.status}`);
    }
    const goal = (await response.json()) as unknown;
    const nextPlan = parseGoalPlanHandleCandidate({ goal });
    if (nextPlan) {
      this.activeGoalPlanOverride = nextPlan;
      this._emitGoalPlanUpdate(nextPlan);
    }
  };

  private async _postGoalAction(
    action: 'pause' | 'resume' | 'cancel',
    body: Record<string, unknown> = {}
  ): Promise<void> {
    const goalPlan = this._resolveGoalPlan();
    if (!goalPlan) throw new Error('No active target is available');
    const response = await workspaceApiFetch(
      `/api/workspace/${encodeURIComponent(this.workspaceId)}/agent-goals/${encodeURIComponent(goalPlan.goalId)}/${action}`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      },
      {
        workspaceId: this.workspaceId,
        category: 'foreground-action',
        mode: 'foreground',
        priority: 'foreground',
      }
    );
    if (!response.ok) {
      throw new Error(`Target action failed with HTTP ${response.status}`);
    }
    const goal = (await response.json()) as unknown;
    const nextPlan = parseGoalPlanHandleCandidate({ goal });
    if (nextPlan) {
      this.activeGoalPlanOverride = nextPlan;
      this._emitGoalPlanUpdate(nextPlan);
    }
    if (action === 'resume') {
      this._startActiveGoalEvents(goalPlan.goalId);
    }
  }

  private readonly _pauseActiveGoal = () => this._postGoalAction('pause');

  private readonly _resumeActiveGoal = (resumeData?: Record<string, unknown>) =>
    this._postGoalAction('resume', resumeData ? { resumeData } : {});

  private readonly _cancelActiveGoal = () =>
    this._postGoalAction('cancel', {
      reason: 'Cancelled by user',
    });

  private _emitGoalPlanUpdate(goalPlan: GoalPlanHandle): void {
    if (typeof window === 'undefined') return;
    const key = `${goalPlan.goalId}:${goalPlan.status ?? ''}:${goalPlan.blocker?.code ?? ''}:${goalPlan.goalTree.progress.completed}:${goalPlan.goalTree.progress.blocked}:${goalPlan.goalTree.progress.invalid}:${goalPlan.goalTree.progress.missing}`;
    if (this.lastEmittedGoalPlanKey === key) return;
    this.lastEmittedGoalPlanKey = key;
    window.dispatchEvent(
      new CustomEvent('streamlain:agent-goal-plan-updated', {
        detail: {
          workspaceId: this.workspaceId,
          goalId: goalPlan.goalId,
          status: goalPlan.status,
          blocker: goalPlan.blocker,
          goalTree: goalPlan.goalTree,
        },
      })
    );
  }

  private _renderGoalOverlay() {
    const goalPlan = this._resolveGoalPlan();
    return html`<div
      class="chat-panel-goal-overlay"
      data-open=${this.goalTreeOpen ? 'true' : 'false'}
    >
      <div
        class="chat-panel-goal-overlay-backdrop"
        @click=${this._closeGoalTree}
      ></div>
      <div class="chat-panel-goal-overlay-panel">
        <goal-tree-panel
          .goalTree=${goalPlan?.goalTree ?? this._resolveGoalTree()}
          .goalStatus=${goalPlan?.status ?? null}
          .goalHistory=${this.goalHistory}
          .goalHistoryLoading=${this.goalHistoryLoading}
          .onClose=${this._closeGoalTree}
          .onSelectGoal=${this._selectGoalFromHistory}
          .onPatch=${this._handleGoalTreePatch}
          .onPause=${this._pauseActiveGoal}
          .onResume=${this._resumeActiveGoal}
          .onCancel=${this._cancelActiveGoal}
        ></goal-tree-panel>
      </div>
    </div>`;
  }

  private readonly _handleCodeSelectionContext = (event: Event) => {
    if (!isCodeSelectionContextEvent(event)) return;
    const selection = event.detail.selection;
    const current = this.chatContextValue.codeSelections || [];
    this.updateContext({
      codeSelections: [
        ...current.filter(item => item.id !== selection.id),
        selection,
      ],
    });
  };

  private readonly updateDraft = async (context: Partial<ChatContextValue>) => {
    if (!this.aiDraftService) {
      return;
    }
    const draft: Partial<AIDraftState> = pick(context, [
      'quote',
      'images',
      'markdown',
    ]);
    if (!Object.keys(draft).length) {
      return;
    }
    await this.aiDraftService.setDraft(draft);
  };

  private readonly initChatContent = async () => {
    this.isHistoryLoading = true;
    await this.updateHistory();
    this.isHistoryLoading = false;
  };

  protected override firstUpdated(): void {}

  private _scrollListenersInitialized = false;
  private _initializeScrollListeners() {
    const chatMessages = this.chatMessagesRef.value;
    if (chatMessages) {
      chatMessages.updateComplete
        .then(() => {
          chatMessages.addEventListener('scrollend', () => {
            this.lastScrollTop = chatMessages.scrollTop;
          });
          this._scrollListenersInitialized = true;
        })
        .catch(console.error);
    }
  }

  protected override updated(changedProperties: PropertyValues) {
    if (
      changedProperties.has('activeWorkflowAttachment') ||
      changedProperties.has('activeWorkflowContextForce')
    ) {
      this._syncActiveWorkflowContext(
        changedProperties.has('activeWorkflowContextForce') &&
          changedProperties.get('activeWorkflowContextForce') !== undefined
      );
    }
    if (changedProperties.has('targetModeEnabled') && !this.targetModeEnabled) {
      if (this.goalTreeOpen) this.goalTreeOpen = false;
      this._syncActiveGoalFromMessages();
    }
    if (changedProperties.has('chatContextValue')) {
      this.historyRefresh.flush();
      this._syncActiveGoalFromMessages();
      const goalPlan = this._resolveGoalPlan();
      if (goalPlan) this._emitGoalPlanUpdate(goalPlan);
    }

    // restore pinned chat scroll position
    if (
      changedProperties.has('host') &&
      this.session?.pinned &&
      this.lastScrollTop !== undefined
    ) {
      this.chatMessagesRef.value?.scrollToPos(this.lastScrollTop);
    }

    if (!this._scrollListenersInitialized) {
      this._initializeScrollListeners();
    }
  }

  override disconnectedCallback(): void {
    this._stopActiveGoalEvents();
    super.disconnectedCallback();
  }

  public openPreviewPanel(content?: TemplateResult<1>) {
    this.showPreviewPanel = true;
    if (content) this.previewPanelContent = content;
    AIProvider.slots.previewPanelOpenChange.next(true);
  }

  public closePreviewPanel(destroyContent: boolean = false) {
    this.showPreviewPanel = false;
    if (destroyContent) this.previewPanelContent = null;
    AIProvider.slots.previewPanelOpenChange.next(false);
  }

  public get isPreviewPanelOpen() {
    return this.showPreviewPanel;
  }

  override connectedCallback() {
    super.connectedCallback();

    window.addEventListener(
      'streamlain:goal-tree-toggle',
      this._handleGoalTreeToggle
    );
    this._disposables.add(() =>
      window.removeEventListener(
        'streamlain:goal-tree-toggle',
        this._handleGoalTreeToggle
      )
    );

    window.addEventListener(
      AI_CODE_SELECTION_CONTEXT_EVENT,
      this._handleCodeSelectionContext
    );
    this._disposables.add(() =>
      window.removeEventListener(
        AI_CODE_SELECTION_CONTEXT_EVENT,
        this._handleCodeSelectionContext
      )
    );

    this.initChatContent().catch(console.error);
    this.subscribeHistoryUpdates();

    if (this.aiDraftService) {
      this.aiDraftService
        .getDraft()
        .then(draft => {
          this.chatContextValue = {
            ...this.chatContextValue,
            ...draft,
          };
        })
        .catch(console.error);
    }

    // revalidate subscription to get the latest status
    this.subscriptionService.subscription.revalidate();

    this._disposables.add(
      AIProvider.slots.actions.subscribe(({ event }) => {
        const { status } = this.chatContextValue;
        if (
          event === 'finished' &&
          (status === 'idle' || status === 'success')
        ) {
          this.updateActions().catch(console.error);
        }
      })
    );

    this._disposables.add(
      AIProvider.slots.requestOpenWithChat.subscribe(
        (params: AIChatParams | null) => {
          if (!params) {
            return;
          }
          const targetsThisChat = params.host
            ? this.host === params.host
            : Boolean(
                params.workspaceId && params.workspaceId === this.workspaceId
              );
          if (!targetsThisChat) return;
          if (params.context) {
            // sl-dpq2.5: Use explicit context (fromAnswer, workflow editing, etc.)
            this.updateContext(params.context);
          } else if (params.host) {
            extractSelectedContent(params.host)
              .then(context => {
                if (!context) return;
                this.updateContext(context);
              })
              .catch(console.error);
          }
          AIProvider.slots.requestOpenWithChat.next(null);
        }
      )
    );
  }

  override render() {
    const messages = html`<ai-chat-messages
      class=${classMap({
        'ai-chat-messages': true,
        'independent-mode': !!this.independentMode,
        'no-message': this.messages.length === 0,
      })}
      ${ref(this.chatMessagesRef)}
      .host=${this.host}
      .workspaceId=${this.workspaceId}
      .docId=${this.docId}
      .session=${this.session}
      .createSession=${this.createSession}
      .chatContextValue=${this.chatContextValue}
      .updateContext=${this.updateContext}
      .isHistoryLoading=${this.isHistoryLoading}
      .extensions=${this.extensions}
      .affineFeatureFlagService=${this.affineFeatureFlagService}
      .affineThemeService=${this.affineThemeService}
      .notificationService=${this.notificationService}
      .aiToolsConfigService=${this.aiToolsConfigService}
      .aiModelService=${this.aiModelService}
      .networkSearchConfig=${this.networkSearchConfig}
      .reasoningConfig=${this.reasoningConfig}
      .width=${this.width}
      .independentMode=${this.independentMode}
      .canvasAIChatContextMode=${this.canvasAIChatContextMode}
      .messages=${this.messages}
      .goalPlan=${this._resolveGoalPlan()}
      .docDisplayService=${this.docDisplayConfig}
      .peekViewService=${this.peekViewService}
      .onOpenDoc=${this.onOpenDoc}
      .workspaceDocSyncEngine=${this.workspaceDocSyncEngine}
      .onOpenSettings=${this.onOpenSettings}
      .noKeysTitle=${this.noKeysTitle}
      .noKeysDescription=${this.noKeysDescription}
      .noKeysButtonText=${this.noKeysButtonText}
      .bots=${this.bots}
      .onSelectBot=${this.onSelectBot}
    ></ai-chat-messages>`;

    const composer = html`<ai-chat-composer
      style=${styleMap({
        [this.onboardingOffsetY > 0 ? 'paddingTop' : 'paddingBottom']:
          `${this.messages.length === 0 ? Math.abs(this.onboardingOffsetY) * 2 : 0}px`,
      })}
      .affineFeatureFlagService=${this.affineFeatureFlagService}
      .independentMode=${this.independentMode}
      .canvasAIChatContextMode=${this.canvasAIChatContextMode}
      .host=${this.host}
      .workspaceId=${this.workspaceId}
      .docId=${this.docId}
      .session=${this.session}
      .createSession=${this.createSession}
      .chatContextValue=${this.chatContextValue}
      .goalPlanOverride=${this._resolveGoalPlan()}
      .targetModeEnabled=${this.targetModeEnabled}
      .goalTabActive=${this.goalTreeOpen}
      .onGoalResume=${this._resumeActiveGoal}
      .onGoalCancel=${this._cancelActiveGoal}
      .onGoalPause=${this._pauseActiveGoal}
      .updateContext=${this.updateContext}
      .externalContextDocs=${this.externalContextDocs}
      .networkSearchConfig=${this.networkSearchConfig}
      .reasoningConfig=${this.reasoningConfig}
      .docDisplayConfig=${this.docDisplayConfig}
      .searchMenuConfig=${this.searchMenuConfig}
      .serverService=${this.serverService}
      .affineWorkspaceDialogService=${this.affineWorkspaceDialogService}
      .notificationService=${this.notificationService}
      .aiDraftService=${this.aiDraftService}
      .aiToolsConfigService=${this.aiToolsConfigService}
      .subscriptionService=${this.subscriptionService}
      .aiModelService=${this.aiModelService}
      .onAISubscribe=${this.onAISubscribe}
      .bots=${this.bots}
      .onSelectBot=${this.onSelectBot}
      .trackOptions=${{
        where: 'chat-panel',
        control: 'chat-send',
      }}
    ></ai-chat-composer>`;

    const right = this.previewPanelContent;

    return html`<chat-panel-split-view
      .left=${html`<div class="chat-panel-main">
        <div class="chat-panel-messages-layer">
          ${messages}
          ${this.targetModeEnabled ? this._renderGoalOverlay() : nothing}
        </div>
        ${composer}
      </div>`}
      .right=${right}
      .open=${this.showPreviewPanel}
    >
    </chat-panel-split-view>`;
  }
}
